import { Menu, Transition } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/solid";
import { SVGElement } from "@ui/types";
import classNames from "classnames";
import Link from "next/link";
import React, { Fragment, ReactNode, useMemo } from "react";

export type MenuOption = {
  label: any;
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  icon?: SVGElement;
  hidden?: boolean;
  selected?: boolean;
  id?: string;
};

export interface ItemContentProps {
  active: boolean;
  option: MenuOption;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

function ItemContent({ active, option, onClick, ...rest }: ItemContentProps) {
  const className = classNames(
    option.selected ? "text-foreground" : "text-muted-foreground",
    "w-full font-medium text-sm hover:bg-muted flex items-center justify-between py-2 px-3",
  );
  const icon = option.icon ? (
    <option.icon className="w-5 h-5 mr-3 text-muted-foreground" aria-hidden="true" />
  ) : null;

  const label =
    typeof option.label === "function" ? option.label({ active, option }) : option.label;

  if (option.href) {
    return (
      <Link href={option.href} {...rest} className={className}>
        {icon}
        {label}
      </Link>
    );
  }

  return (
    <button
      className={className}
      {...rest}
      onClick={(e) => {
        if (typeof onClick === "function") {
          onClick(e);
        }
        if (typeof option.onClick === "function") {
          option.onClick(e);
        }
      }}
    >
      <span>
        {icon}
        {label}
      </span>
      {option.selected ? <CheckIcon className="w-3 h-3 ml-4 text-purple-600" /> : null}
    </button>
  );
}

export interface DropdownMenuProps {
  children?: ReactNode;
  options: Array<MenuOption | MenuOption[]> | undefined;
  onClick?: (e: any) => void;
  label?: any;
  disabled?: boolean;
}

export function DropdownMenu({
  options = [],
  label = "Options",
  disabled: _disabled,
}: DropdownMenuProps) {
  const _isDisabled = _disabled || options.length === 0;
  const parsedLabel = typeof label === "function" ? label() : label;

  const filteredOptions = useMemo(() => {
    const results: DropdownMenuProps["options"] = [];
    options.forEach((option) => {
      if (Array.isArray(option)) {
        const filtered = option.filter((o) => !o.hidden);
        if (filtered.length > 0) {
          results.push(filtered);
        }
        return;
      }
      if (!option.hidden) {
        results.push(option);
      }
    });
    return results;
  }, [options]);

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="flex items-center truncate">
        <span className="hover:underline text-foreground ml-2 text-sm font-medium truncate">
          {parsedLabel}
        </span>
        <ChevronDownIcon className="text-foreground w-4 h-4 ml-1" aria-hidden="true" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="min-w-max shrink-0 focus:outline-none absolute right-0 z-10 mt-[calc(1rem+1px)] origin-top-right bg-background border border-border divide-y divide-border rounded-sm shadow-md">
          {filteredOptions.map((option, index) => (
            <div key={`group-${index}`}>
              {Array.isArray(option) ? (
                option.map((groupedOption, innerIndex) => (
                  <Menu.Item key={`group-${index}-option-${innerIndex}`}>
                    {({ active }) => <ItemContent active={active} option={groupedOption} />}
                  </Menu.Item>
                ))
              ) : (
                <Menu.Item key={index}>
                  {({ active }) => <ItemContent active={active} option={option} />}
                </Menu.Item>
              )}
            </div>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

export default DropdownMenu;
